import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { errMessage } from "../lib/errors";
import { Star, ShieldCheck, MessageSquarePlus, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DishReviewsSection — full list + inline "write a review" form inside the
 * dish detail modal.
 *
 * States:
 *  - Loading (skeleton)
 *  - No reviews yet (prompt to be first)
 *  - Some reviews (list + write-your-own inline form for signed-in users)
 *  - Signed-out with reviews (list + "Sign in to review" nudge)
 */
function StarRow({ value, size = 14, interactive = false, onChange }) {
  const filled = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= filled;
        const Star_ = interactive ? "button" : "span";
        return (
          <Star_
            key={n}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => onChange?.(n) : undefined}
            className={interactive ? "hover:scale-110 transition-transform" : ""}
            aria-label={interactive ? `Rate ${n} stars` : undefined}
          >
            <Star
              size={size}
              className={on ? "text-[var(--mm-accent)] fill-[var(--mm-accent)]" : "text-[var(--mm-cream)]/25"}
              strokeWidth={1.6}
            />
          </Star_>
        );
      })}
    </div>
  );
}

export default function DishReviewsSection({ lookupKey, onChange }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    if (!lookupKey) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dishes/${lookupKey}/reviews?limit=20`);
      setData(data);
    } catch {
      setData({ count: 0, average: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }, [lookupKey]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/dishes/${lookupKey}/reviews`,
        { rating, comment: comment.trim() || undefined },
        { withCredentials: true, headers: withCsrf() }
      );
      toast({ title: "Thanks!", description: "Your review is live." });
      setShowForm(false);
      setComment("");
      await load();
      onChange?.(); // let the parent refresh menu-level ratings
    } catch (e) {
      const detail = errMessage(e, "Couldn't submit — please try again.");
      toast({ title: "Review failed", description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  function withCsrf() {
    if (typeof document === "undefined") return {};
    const m = document.cookie.match(/(?:^|; )mm_csrf=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  }

  if (loading) {
    return (
      <div className="mt-8 pt-6 border-t border-[var(--mm-cream)]/10" data-testid="reviews-loading">
        <div className="h-4 w-24 bg-[var(--mm-cream)]/10 mb-3 animate-pulse" />
        <div className="h-3 w-full bg-[var(--mm-cream)]/10 animate-pulse" />
      </div>
    );
  }

  const { count = 0, average = 0, items = [] } = data || {};

  return (
    <div className="mt-8 pt-6 border-t border-[var(--mm-cream)]/10" data-testid="dish-reviews">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="section-label mb-1.5">Diner ratings</p>
          {count > 0 ? (
            <div className="flex items-center gap-2">
              <StarRow value={average} size={16} />
              <span className="text-[var(--mm-cream)]">{average.toFixed(1)}</span>
              <span className="text-[var(--mm-cream)]/50 text-sm">· {count} review{count === 1 ? "" : "s"}</span>
            </div>
          ) : (
            <p className="text-sm text-[var(--mm-cream)]/50 italic">No reviews yet — be the first.</p>
          )}
        </div>
        {user && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="text-[0.68rem] tracking-[0.24em] uppercase text-[var(--mm-accent)] hover:underline flex items-center gap-1.5"
            data-testid="write-review-btn"
          >
            <MessageSquarePlus size={12} /> Write a review
          </button>
        )}
      </div>

      {/* Signed-out prompt */}
      {!user && (
        <p className="text-xs text-[var(--mm-cream)]/50 mb-4 italic">
          <a href="/login" className="text-[var(--mm-accent)] underline">Sign in</a> to leave a review — it takes 10 seconds.
        </p>
      )}

      {/* Write form */}
      {showForm && (
        <form
          onSubmit={submit}
          className="mb-6 p-4 bg-[var(--mm-bg-alt)]/70 border border-[var(--mm-cream)]/12"
          data-testid="review-form"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream)]/60">Your rating</span>
            <StarRow value={rating} size={20} interactive onChange={setRating} />
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="What did you love? (optional)"
            rows={3}
            data-testid="review-comment"
            className="w-full bg-[var(--mm-bg)]/60 border border-[var(--mm-cream)]/12 focus:border-[var(--mm-accent)] outline-none px-3 py-2 text-sm text-[var(--mm-cream)] placeholder:text-[var(--mm-cream)]/35 resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-[var(--mm-cream)]/40">{comment.length}/500</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-[0.68rem] tracking-[0.24em] uppercase text-[var(--mm-cream)]/60 hover:text-[var(--mm-cream)] px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit"
                className="bg-[var(--mm-accent)] text-[var(--mm-bg)] text-[0.68rem] tracking-[0.24em] uppercase font-semibold px-4 py-1.5 hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : null} Post review
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Review list */}
      {items.length > 0 && (
        <ul className="space-y-4">
          {items.map((r) => (
            <li key={r.id || r.created_at} className="pb-4 border-b border-[var(--mm-cream)]/8 last:border-b-0">
              <div className="flex items-center gap-2 mb-1.5">
                <StarRow value={r.rating} size={12} />
                <span className="text-[var(--mm-cream)]/85 text-sm">{r.user_first_name}</span>
                {r.verified_purchase && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] tracking-[0.22em] uppercase text-[var(--mm-accent)] border border-[var(--mm-accent)]/40 px-1.5 py-0.5"
                    data-testid="verified-badge"
                  >
                    <ShieldCheck size={9} /> Verified
                  </span>
                )}
              </div>
              {r.comment && (
                <p className="text-sm text-[var(--mm-cream)]/70 leading-relaxed">{r.comment}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
